import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { apiBaseUrl } from '../config/runtime';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState<'email' | 'verify' | 'reset'>('email');
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const response = await axios.post(apiBaseUrl('/auth/forgot-password'), {
        email
      });

      setMessage(response.data.message || t('forgotPw.sent'));
      setStep('verify');
      console.log('✅ 인증번호 발송 성공:', response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || t('forgotPw.sendFailed'));
      console.error('❌ 인증번호 발송 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const response = await axios.post(apiBaseUrl('/auth/verify-reset-code'), {
        email,
        code: verificationCode
      });

      setMessage(t('forgotPw.verified'));
      setStep('reset');
      console.log('✅ 인증번호 확인 성공:', response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || t('forgotPw.wrongCode'));
      console.error('❌ 인증번호 확인 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (newPassword.length < 6) {
      setError(t('forgotPw.pwTooShort'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('forgotPw.pwMismatch'));
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(apiBaseUrl('/auth/reset-password'), {
        email,
        code: verificationCode,
        newPassword
      });

      setMessage(t('forgotPw.changed'));
      console.log('✅ 비밀번호 재설정 성공:', response.data);
      
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || t('profile.pwChangeFailed'));
      console.error('❌ 비밀번호 재설정 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        padding: '40px',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        width: '100%',
        maxWidth: '450px'
      }}>
        <h2 style={{ textAlign: 'center', marginBottom: '30px', color: '#333' }}>
          🔐 {t('login.findPassword')}
        </h2>

        {/* 단계 표시 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px' }}>
          <div style={{ 
            flex: 1, 
            textAlign: 'center',
            color: step === 'email' ? '#667eea' : '#999',
            fontWeight: step === 'email' ? 'bold' : 'normal'
          }}>
            1. {t('login.email')}
          </div>
          <div style={{ 
            flex: 1, 
            textAlign: 'center',
            color: step === 'verify' ? '#667eea' : '#999',
            fontWeight: step === 'verify' ? 'bold' : 'normal'
          }}>
            2. {t('forgotPw.step2')}
          </div>
          <div style={{ 
            flex: 1, 
            textAlign: 'center',
            color: step === 'reset' ? '#667eea' : '#999',
            fontWeight: step === 'reset' ? 'bold' : 'normal'
          }}>
            3. {t('forgotPw.step3')}
          </div>
        </div>

        {error && (
          <div style={{
            background: '#ffe6e6',
            color: '#d32f2f',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {message && (
          <div style={{
            background: '#e6f7e6',
            color: '#2e7d32',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {message}
          </div>
        )}

        {/* Step 1: 이메일 입력 */}
        {step === 'email' && (
          <form onSubmit={handleSendCode}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: '#555',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {t('register.email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('forgotPw.emailPlaceholder')}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                background: loading ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s'
              }}
            >
              {loading ? t('forgotPw.sending') : t('forgotPw.getCode')}
            </button>
          </form>
        )}

        {/* Step 2: 인증번호 입력 */}
        {step === 'verify' && (
          <form onSubmit={handleVerifyCode}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: '#555',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {t('forgotPw.codeLabel')}
              </label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                placeholder={t('forgotPw.codePlaceholder')}
                required
                maxLength={6}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '18px',
                  letterSpacing: '4px',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}
              />
              <div style={{ marginTop: '10px', fontSize: '13px', color: '#666' }}>
                💡 {t('forgotPw.notReceived')}{' '}
                <button
                  type="button"
                  onClick={() => setStep('email')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#667eea',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    padding: 0
                  }}
                >
                  {t('forgotPw.resend')}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || verificationCode.length !== 6}
              style={{
                width: '100%',
                padding: '14px',
                background: (loading || verificationCode.length !== 6) ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: (loading || verificationCode.length !== 6) ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s'
              }}
            >
              {loading ? t('forgotPw.checking') : t('forgotPw.verifyCode')}
            </button>
          </form>
        )}

        {/* Step 3: 새 비밀번호 입력 */}
        {step === 'reset' && (
          <form onSubmit={handleResetPassword}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: '#555',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {t('profile.newPw')}
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('forgotPw.newPwPlaceholder')}
                required
                minLength={6}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: '#555',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {t('forgotPw.confirmLabel')}
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('forgotPw.confirmPlaceholder')}
                required
                minLength={6}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                background: loading ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s'
              }}
            >
              {loading ? t('profile.changing') : t('profile.changePw')}
            </button>
          </form>
        )}

        {/* 로그인으로 돌아가기 */}
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <button
            onClick={() => navigate('/login')}
            style={{
              background: 'none',
              border: 'none',
              color: '#667eea',
              cursor: 'pointer',
              fontSize: '14px',
              textDecoration: 'underline'
            }}
          >
            ← {t('findId.backToLogin')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
